import { DestroyRef, inject, Injectable } from '@angular/core';
import { OverlayService } from '../../../shared/services/overlay.service';
import { CustomersService } from './customers.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { AchMethodModel, CustomerModel, PaymentMethodModel } from '../customer.model';
import { tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PaymentCollectStore } from '../../payment/store/payment-collect.store';

@Injectable({
  providedIn: 'root'
})
export class ActionsService {
  protected overlayService = inject(OverlayService);
  protected customerService = inject(CustomersService);
  protected notificationService = inject(NotificationService);
  protected destroyRef = inject(DestroyRef);
  protected store = inject(PaymentCollectStore);

  public handleCustomerSaveAction(
    customer: CustomerModel,
    formData: any,
    successCallback?: () => void,
    errorCallback?: () => void,
  ): void {

    const customerModel: CustomerModel = {
      id: customer.id,
      contactFirstName: <string>formData!.contactFirstName,
      contactLastName: <string>formData!.contactLastName,
      displayName: <string>formData!.displayName,
      companyName: <string>formData!.companyName,
      companyAccountNumber: <string>formData!.companyAccountNumber,
      contactEmail: <string>formData!.contactEmail,
      contactPhoneNumber: <string>formData!.businessPhone + ' ' + <string>formData!.businessPhoneExt,
      mobilePhoneNumber: <string>formData!.mobilePhoneNumber,
      notes: <string>formData!.notes,
      customerSettings: customer.customerSettings,
      customerAddress: <string>formData!.customerAddress,
      customerCity: <string>formData!.customerCity,
      customerState: <string>formData!.customerState,
      customerZip: <string>formData!.customerZip,
      parentCustomerId: <string>formData!.parentCustomerId,
      // Free-text salesperson id; empty input persists as null (default).
      salesPersonId: (<string>formData!.salesPersonId)?.trim() || null,
      paymentTerms: (<string>formData!.paymentTerms)?.trim() || null,
      // The payload is an explicit whitelist, so a field missing here silently
      // never reaches the API however well the form is wired. Passed through as
      // undefined rather than defaulted to false when the caller's form has no
      // toggle: JSON.stringify drops the key, and the backend preserves the
      // stored value on absence — so a form without the control cannot clear it.
      isTestCustomer: <boolean | undefined>formData!.isTestCustomer,
      // From the loaded customer, not the form — same as customerSettings above.
      // The API coerces an absent `inactive` to false, so omitting these here
      // reactivates every ERP-retired customer that gets edited.
      inactive: customer.inactive,
      inactiveDate: customer.inactiveDate
    };


    if (!customerModel.contactEmail) {
      delete customerModel.contactEmail;
    }
    if(customerModel.contactFirstName?.trim() == ''){
      delete customerModel.contactFirstName;
    }
    if(customerModel.contactLastName?.trim() == ''){
      delete customerModel.contactLastName;
    }
    if(customerModel.contactEmail?.trim() == ''){
      delete customerModel.contactEmail;
    }
    if(customerModel.mobilePhoneNumber == ' '){
      delete customerModel.mobilePhoneNumber;
    }
    if(customerModel.contactPhoneNumber == ' '){
      delete customerModel.contactPhoneNumber;
    }
    if(customerModel.mobilePhoneNumber == ''){
      delete customerModel.mobilePhoneNumber;
    }
    if(customerModel.contactPhoneNumber == ''){
      delete customerModel.contactPhoneNumber;
    }
    if(customerModel.customerAddress?.trim() == ''){
      delete customerModel.customerAddress;
    }
    if(customerModel.customerCity?.trim() == ''){
      delete customerModel.customerCity;
    }
    if(customerModel.customerState?.trim() == ''){
      delete customerModel.customerState;
    }
    if(customerModel.customerZip?.trim() == ''){
      delete customerModel.customerZip;
    }
    if(customerModel.parentCustomerId?.trim() == ''){
      delete customerModel.parentCustomerId;
    }


    this.customerService.updateCustomer(<string>customer.id, customerModel)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.overlayService.showOverlay())
      )
      .subscribe({
        next: (customer) => {
          this.store.reloadCustomers();

          if (successCallback instanceof Function) {
            successCallback();
          }
          this.overlayService.hideOverlay();
          this.notificationService.success('Customer updated successfully');
        },
        error: (error) => {
          this.overlayService.hideOverlay()
          this.notificationService.error(error.message);
          if (errorCallback instanceof Function) {
            errorCallback();
          }
        }
      });
  }

  public handleCustomerDeleteAction(
    customer: CustomerModel,
    successCallback?: () => void,
  ): void {
    this.customerService
      .deleteCustomer(<string>customer.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.overlayService.showOverlay())
      )
      .subscribe({
        next: (customer) => {
          this.store.reloadCustomers();
          if (successCallback instanceof Function) {
            successCallback();
          }
          this.overlayService.hideOverlay();
          this.notificationService.success('Customer has been deleted successfully');

        },
        error: (error) => {
          this.overlayService.hideOverlay()
          this.notificationService.error(error.message);
        }
      });
  }

  /**
   * Merge (migrate) the source customer into the surviving customer. On success
   * all related records have been re-pointed to the surviving customer and the
   * source has been purged, so the customer list is reloaded.
   */
  public handleCustomerMigrateAction(
    sourceCustomerId: string,
    survivingCustomerId: string,
    successCallback?: () => void,
  ): void {
    this.customerService
      .migrateCustomer(sourceCustomerId, survivingCustomerId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.overlayService.showOverlay())
      )
      .subscribe({
        next: () => {
          this.store.reloadCustomers();
          if (successCallback instanceof Function) {
            successCallback();
          }
          this.overlayService.hideOverlay();
          this.notificationService.success('Customer migrated successfully');
        },
        error: (error) => {
          this.overlayService.hideOverlay();
          this.notificationService.error(error?.error?.message || error.message);
        }
      });
  }

  public handlePaymentDeleteAction(
    paymentMethod: PaymentMethodModel,
    successCallback?: () => void,
  ): void {
    this.customerService
      .deletePaymentMethod(<string>paymentMethod!.customerId, <string>paymentMethod!.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.overlayService.showOverlay())
      )
      .subscribe({
        next: () => {
          this.store.reloadPaymentMethods();
          
          if (successCallback instanceof Function) {
            successCallback();
          }
          this.overlayService.hideOverlay();
          this.notificationService.success('Payment has been deleted successfully');
        },
        error: (error) => {
          this.overlayService.hideOverlay()
          this.notificationService.error(error.message);
        }
      });
  }

  public handlePaymentSetDefaultAction(
    paymentMethod: PaymentMethodModel,
    successCallback?: () => void,
  ): void {
    //console.log('Setting default payment method:', paymentMethod);
    //console.log('Payment method ID:', paymentMethod?.id);
    //console.log('Customer ID:', paymentMethod?.customerId);
    
    this.customerService
      .setDefaultPaymentMethod(<string>paymentMethod!.customerId, <string>paymentMethod!.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.overlayService.showOverlay();
        })
      )
      .subscribe({
        next: () => {
          //console.log('API call successful', paymentMethod);
          this.store.reloadPaymentMethods();
          
          if (successCallback instanceof Function) {
            successCallback();
          }
          this.overlayService.hideOverlay();
          this.notificationService.success('Payment has been set as default successfully');
        },
        error: (error: any) => {
          console.error('API call failed:', error);
          this.overlayService.hideOverlay()
          this.notificationService.error(error.message);
        }
      });
  }
}
